// @contexts/LifecycleClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { LifecycleClient } from '@services/clients/LifecycleClient';
import { fromInteropLifecycleClient } from '@services/clients/LifecycleClient';
import { createDefaultLifecycleClient as createInteropLifecycleClient } from "@src/purescript/interop";
import { StubLifecycleClient } from '@services/stubs/StubLifecycleClient';
import { enabledFeatures, FeaturesEnum } from '@src/utils/enabledFeatures';
import { useJwtController } from '@contexts/Jwt.context';
import { requireContext } from '@contexts/requireContext';

const LifecycleClientContext = createContext<OptionTs<LifecycleClient>>(Option.none());

export const LifecycleClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { markUnauthorized } = useJwtController();

    // Create the lifecycle client, conditionally using the StubLifecycleClient if the flag is set
    const lifecycleClient = useMemo<LifecycleClient>(() => {
        if (enabledFeatures[FeaturesEnum.StubClients]) {
            return new StubLifecycleClient();
        }
        return fromInteropLifecycleClient(createInteropLifecycleClient(markUnauthorized));
    }, [markUnauthorized]);

    const contextValue = useMemo(
        () => Option.some(lifecycleClient),
        [lifecycleClient]
    );

    return (
        <LifecycleClientContext.Provider value={contextValue}>
            {children}
        </LifecycleClientContext.Provider>
    );
};

export const useLifecycleClient = (): LifecycleClient => {
    return requireContext(
        useContext(LifecycleClientContext),
        'useLifecycleClient must be used within a LifecycleClientProvider',
    );
};
